import { useMemo } from 'react';
import { useAuthoringStandards, type AuthoringStandards } from '../../api/authoring.js';

/**
 * AuthoringStandardsPage — a read-only reference for "the rules" of
 * building on the platform.
 *
 * This is the same catalog that the in-app Copilot consumes as its
 * system prompt foundation and that the (future) Claude Code MCP
 * path will expose as the get_authoring_standards tool. Rendering it
 * in the app gives authors a single source of truth — what helpers
 * exist, what widget configs are required, what audiences imply
 * for branding, what drill-through params look like.
 */
export function AuthoringStandardsPage(): JSX.Element {
  const { data, isLoading, error } = useAuthoringStandards();

  return (
    <>
      <header className="page-title">
        <div>
          <h1>Authoring standards</h1>
          <p className="page-title-subtitle">
            Machine-readable catalog of the platform's authoring rules. Copilot and (soon) Claude
            Code both consume this as the single source of truth — when it changes, every surface
            picks it up automatically.
          </p>
        </div>
        {data && (
          <div className="page-title-actions">
            <span className="badge badge--neutral">v{data.version}</span>
            <span className="text-dim text-xs">Updated {data.lastUpdated}</span>
          </div>
        )}
      </header>

      {isLoading && <div className="text-dim">Loading standards…</div>}
      {error && <div className="alert alert--danger">Failed to load authoring standards.</div>}
      {data && <StandardsContent standards={data} />}
    </>
  );
}

function StandardsContent({ standards }: { standards: AuthoringStandards }): JSX.Element {
  return (
    <div className="standards-page">
      <HelpersSection helpers={standards.helpers} />
      <WidgetTypesSection widgets={standards.widgetTypes} />
      <AudiencesSection audiences={standards.audiences} />
      <PageOptionsSection options={standards.pageOptions} />
      <FormatsAndAggregationsSection
        formats={standards.numberFormats}
        aggregations={standards.aggregations}
      />
      <FiltersAndDrillsSection
        filters={standards.filterKinds}
        drillTargets={standards.drillTargets}
      />
      <RenderLibrariesSection libraries={standards.renderLibraries} />
      <LayoutKindsSection layoutKinds={standards.layoutKinds} />
      <MachineReadableSection raw={standards} />

      <StandardsStyles />
    </div>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────────

function HelpersSection({ helpers }: { helpers: AuthoringStandards['helpers'] }): JSX.Element {
  const byCategory = useMemo(() => {
    const m = new Map<string, AuthoringStandards['helpers']>();
    for (const h of helpers) {
      if (!m.has(h.category)) m.set(h.category, []);
      m.get(h.category)!.push(h);
    }
    return m;
  }, [helpers]);

  return (
    <section className="standards-section">
      <header>
        <h2>Handlebars helpers</h2>
        <p className="text-dim text-sm">
          Available in every HTML layout and widget markdown. Categorized for skimming; use the
          signature and example as copy-paste references.
        </p>
      </header>
      {[...byCategory.entries()].map(([cat, list]) => (
        <div key={cat} className="standards-subsection">
          <h3>{categoryLabel(cat)}</h3>
          <table className="standards-table">
            <thead>
              <tr>
                <th style={{ width: '26%' }}>Signature</th>
                <th>Description</th>
                <th style={{ width: '30%' }}>Example</th>
              </tr>
            </thead>
            <tbody>
              {list.map((h) => (
                <tr key={h.name}>
                  <td>
                    <code className="standards-sig">{h.signature}</code>
                  </td>
                  <td>{h.description}</td>
                  <td>
                    <code className="standards-example">{h.example}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}

function WidgetTypesSection({
  widgets,
}: {
  widgets: AuthoringStandards['widgetTypes'];
}): JSX.Element {
  return (
    <section className="standards-section">
      <header>
        <h2>Widget types</h2>
        <p className="text-dim text-sm">
          Available in the drag-and-drop dashboard designer. Required config fields must be set
          before the widget renders.
        </p>
      </header>
      <table className="standards-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Description</th>
            <th>Default size</th>
            <th>Binding</th>
            <th>Drill</th>
            <th>Required config</th>
          </tr>
        </thead>
        <tbody>
          {widgets.map((w) => (
            <tr key={w.type}>
              <td>
                <span className="badge badge--neutral">{w.type}</span>
              </td>
              <td>{w.description}</td>
              <td className="text-mono text-xs">
                {w.typicalSize.w}×{w.typicalSize.h}
              </td>
              <td>{w.requiresBinding ? '✓' : '—'}</td>
              <td>{w.supportsDrillThrough ? '✓' : '—'}</td>
              <td className="text-mono text-xs">{w.requiredConfigFields.join(', ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function AudiencesSection({
  audiences,
}: {
  audiences: AuthoringStandards['audiences'];
}): JSX.Element {
  return (
    <section className="standards-section">
      <header>
        <h2>Audiences</h2>
        <p className="text-dim text-sm">
          Drive branding overlays and the publish role gate. Tenant-level policies under /admin
          override the Canopy-shipped defaults.
        </p>
      </header>
      <table className="standards-table">
        <thead>
          <tr>
            <th>Audience</th>
            <th>Description</th>
            <th>Publish role</th>
            <th>Overlays</th>
          </tr>
        </thead>
        <tbody>
          {audiences.map((a) => (
            <tr key={a.name}>
              <td>
                <span className={`badge badge--audience badge--audience-${a.name}`}>{a.name}</span>
              </td>
              <td>{a.description}</td>
              <td>
                {a.publishRole ? (
                  <code>{a.publishRole}</code>
                ) : (
                  <span className="text-dim">any author</span>
                )}
              </td>
              <td className="text-xs">
                {a.defaultOverlays.header && 'header '}
                {a.defaultOverlays.footer && 'footer '}
                {a.defaultOverlays.watermark && 'watermark'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PageOptionsSection({
  options,
}: {
  options: AuthoringStandards['pageOptions'];
}): JSX.Element {
  return (
    <section className="standards-section">
      <header>
        <h2>Page options (PDF render)</h2>
      </header>
      <div className="standards-grid">
        <div>
          <h4>Sizes</h4>
          <div className="chip-group">
            {options.sizes.map((s) => (
              <span key={s} className="chip">
                {s}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h4>Orientations</h4>
          <div className="chip-group">
            {options.orientations.map((o) => (
              <span key={o} className="chip">
                {o}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h4>Margins (inches)</h4>
          <p className="text-sm">
            Range {options.marginsInches.min}–{options.marginsInches.max}; default{' '}
            {options.marginsInches.default}.
          </p>
        </div>
      </div>
    </section>
  );
}

function FormatsAndAggregationsSection({
  formats,
  aggregations,
}: {
  formats: AuthoringStandards['numberFormats'];
  aggregations: AuthoringStandards['aggregations'];
}): JSX.Element {
  return (
    <section className="standards-section standards-two-col">
      <div>
        <header>
          <h2>Number formats</h2>
        </header>
        <table className="standards-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Example</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {formats.map((f) => (
              <tr key={f.name}>
                <td>
                  <code>{f.name}</code>
                </td>
                <td className="text-mono text-xs">{f.example}</td>
                <td>{f.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <header>
          <h2>Aggregations</h2>
        </header>
        <table className="standards-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {aggregations.map((a) => (
              <tr key={a.name}>
                <td>
                  <code>{a.name}</code>
                </td>
                <td>{a.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FiltersAndDrillsSection({
  filters,
  drillTargets,
}: {
  filters: AuthoringStandards['filterKinds'];
  drillTargets: AuthoringStandards['drillTargets'];
}): JSX.Element {
  return (
    <section className="standards-section standards-two-col">
      <div>
        <header>
          <h2>Filter kinds (cross-widget)</h2>
        </header>
        <table className="standards-table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Description</th>
              <th>Value shape</th>
            </tr>
          </thead>
          <tbody>
            {filters.map((f) => (
              <tr key={f.kind}>
                <td>
                  <code>{f.kind}</code>
                </td>
                <td>{f.description}</td>
                <td className="text-xs text-mono">{f.defaultValueShape}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <header>
          <h2>Drill-through contract</h2>
        </header>
        <div className="standards-subsection">
          <h4>Kinds</h4>
          <div className="chip-group">
            {drillTargets.kinds.map((k) => (
              <span key={k} className="chip">
                {k}
              </span>
            ))}
          </div>
        </div>
        <div className="standards-subsection">
          <h4>Param kinds</h4>
          <div className="chip-group">
            {drillTargets.paramKinds.map((k) => (
              <span key={k} className="chip">
                {k}
              </span>
            ))}
          </div>
        </div>
        <div className="standards-subsection">
          <h4>Param map syntax</h4>
          <ul className="standards-list">
            <li>
              <code>row.&lt;field&gt;</code> —{' '}
              {drillTargets.paramMapSyntax.rowField.split(' — ')[1]}
            </li>
            <li>
              <code>const:&lt;value&gt;</code> —{' '}
              {drillTargets.paramMapSyntax.literal.split(' — ')[1]}
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function RenderLibrariesSection({
  libraries,
}: {
  libraries: AuthoringStandards['renderLibraries'];
}): JSX.Element {
  return (
    <section className="standards-section">
      <header>
        <h2>Render libraries</h2>
        <p className="text-dim text-sm">
          Declare under <code>layout.libraries</code>. Chromium enables JS only when a library that
          requires it is declared.
        </p>
      </header>
      <table className="standards-table">
        <thead>
          <tr>
            <th>Library</th>
            <th>Description</th>
            <th>JS?</th>
            <th>Version</th>
            <th>Usage</th>
          </tr>
        </thead>
        <tbody>
          {libraries.map((l) => (
            <tr key={l.name}>
              <td>
                <code>{l.name}</code>
              </td>
              <td>{l.description}</td>
              <td>{l.requiresJs ? 'yes' : 'no'}</td>
              <td className="text-xs text-mono">{l.bundledVersion}</td>
              <td>
                <code className="text-xs">{l.usage}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function LayoutKindsSection({
  layoutKinds,
}: {
  layoutKinds: AuthoringStandards['layoutKinds'];
}): JSX.Element {
  return (
    <section className="standards-section">
      <header>
        <h2>Layout kinds</h2>
      </header>
      <table className="standards-table">
        <thead>
          <tr>
            <th>Surface</th>
            <th>Kind</th>
            <th>Description</th>
            <th>Output</th>
          </tr>
        </thead>
        <tbody>
          {layoutKinds.map((l, i) => (
            <tr key={`${l.surface}-${l.kind}-${i}`}>
              <td>{l.surface}</td>
              <td>
                <code>{l.kind}</code>
              </td>
              <td>{l.description}</td>
              <td className="text-xs text-mono">{l.output.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function MachineReadableSection({ raw }: { raw: AuthoringStandards }): JSX.Element {
  return (
    <section className="standards-section">
      <header>
        <h2>Raw JSON</h2>
        <p className="text-dim text-sm">
          The exact shape the in-app Copilot consumes and that the (future)
          <code> get_authoring_standards</code> MCP tool will return.
        </p>
      </header>
      <details>
        <summary>Show JSON</summary>
        <pre className="standards-raw">{JSON.stringify(raw, null, 2)}</pre>
      </details>
    </section>
  );
}

function categoryLabel(cat: string): string {
  switch (cat) {
    case 'formatting':
      return 'Formatting';
    case 'arithmetic':
      return 'Arithmetic';
    case 'logic':
      return 'Logic';
    case 'aggregation':
      return 'Aggregation';
    case 'iteration':
      return 'Iteration';
    case 'data-shape':
      return 'Data shaping';
    case 'links':
      return 'Links & drill-through';
    default:
      return cat;
  }
}

function StandardsStyles(): JSX.Element {
  return (
    <style>{`
      .standards-page {
        display: flex;
        flex-direction: column;
        gap: var(--space-5);
      }
      .standards-section {
        background: var(--color-surface-1);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        padding: var(--space-4);
      }
      .standards-section > header { margin-bottom: var(--space-3); }
      .standards-section h2 { margin: 0 0 4px; font-size: var(--font-size-lg); }
      .standards-section h3 {
        margin: var(--space-3) 0 var(--space-2);
        font-size: var(--font-size-sm);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-secondary);
      }
      .standards-section h4 {
        margin: 0 0 var(--space-2);
        font-size: var(--font-size-sm);
      }
      .standards-subsection { margin-bottom: var(--space-3); }
      .standards-two-col {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
        gap: var(--space-3);
      }
      .standards-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: var(--space-3);
      }
      .standards-table {
        width: 100%;
        border-collapse: collapse;
        font-size: var(--font-size-sm);
      }
      .standards-table th {
        text-align: left;
        padding: 6px 8px;
        border-bottom: 1px solid var(--color-border-subtle);
        color: var(--color-text-secondary);
        font-size: var(--font-size-xs);
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      .standards-table td {
        padding: 8px 8px;
        border-bottom: 1px solid var(--color-border-subtle);
        vertical-align: top;
      }
      .standards-sig, .standards-example {
        font-family: var(--font-mono);
        font-size: 11px;
        background: var(--color-surface-2);
        padding: 2px 6px;
        border-radius: 4px;
        white-space: nowrap;
      }
      .standards-example { white-space: normal; }
      .standards-list {
        list-style: none;
        padding: 0;
        margin: 0;
        font-size: var(--font-size-sm);
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .standards-raw {
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        padding: var(--space-3);
        font-size: 11px;
        max-height: 420px;
        overflow: auto;
      }
    `}</style>
  );
}
